import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import styles from './PhotoTask.module.css';

const PhotoTask = () => {
  const [task, setTask] = useState(null);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState('password');
  const [password, setPassword] = useState('');
  const [teamName, setTeamName] = useState('');
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [galleryTeam, setGalleryTeam] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const { taskId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTaskDetails();
  }, [taskId]);

  // Live camera lifecycle: open the stream while the camera modal is showing,
  // and always stop every track on close / flip / unmount so the camera light
  // doesn't stay on.
  useEffect(() => {
    if (!showCamera) return;
    let cancelled = false;

    const start = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setShowCamera(false);
        setError("Your browser doesn't support live camera capture. Please open this page in Safari or Chrome on your phone.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Camera error:', err);
        if (!cancelled) {
          setShowCamera(false);
          setError("We couldn't access your camera. Please allow camera access in your browser, then tap Take Photo again.");
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [showCamera, facingMode]);

  const fetchTaskDetails = async () => {
    try {
      const response = await axios.get(`/api/tasks/${taskId}`);
      if (response.data.type !== 'photo-recreation') {
        navigate(`/hunt/${taskId}`);
        return;
      }
      setTask(response.data);
    } catch (error) {
      console.error('Error fetching task details:', error);
      setError('Failed to fetch task details. Please try again.');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`/api/tasks/${taskId}/authenticate`, { password });
      if (response.data.authenticated) {
        setStage('teamName');
        setError(null);
      } else {
        setError('Incorrect password. Please try again.');
      }
    } catch (error) {
      console.error('Error authenticating:', error);
      setError('Failed to authenticate. Please try again.');
    }
  };

  const handleTeamNameSubmit = (e) => {
    e.preventDefault();
    if (/^[a-zA-Z0-9\s'''‛`-]+$/.test(teamName)) {
      setStage('photos');
      setError(null);
    } else {
      setError("Team name can only contain letters, numbers, spaces, apostrophes, and hyphens.");
    }
  };

  const handleOpenCamera = () => {
    setError(null);
    setShowCamera(true);
  };

  const handleFlipCamera = () => {
    // The effect tears down the current stream and starts the other camera.
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  const handleCancelCamera = () => {
    setShowCamera(false);
  };

  // Capture the centre square of the live frame — matching the square,
  // object-fit:cover preview — so what the team sees is exactly what uploads.
  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const side = Math.min(video.videoWidth, video.videoHeight);
    const sx = (video.videoWidth - side) / 2;
    const sy = (video.videoHeight - side) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = side;
    canvas.height = side;
    const ctx = canvas.getContext('2d');
    // Front camera preview is mirrored; mirror the capture to match it.
    if (facingMode === 'user') {
      ctx.translate(side, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, sx, sy, side, side, 0, 0, side, side);

    setIsUploading(true);
    setShowCamera(false);
    setError(null);

    canvas.toBlob(async (blob) => {
      const formData = new FormData();
      formData.append('photo', blob, 'photo.jpg');
      formData.append('teamName', teamName);
      formData.append('photoIndex', currentPhotoIndex + 1);

      try {
        const res = await axios.post(`/api/tasks/${taskId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploadedPhotos([...uploadedPhotos, currentPhotoIndex]);
        if (res.data && res.data.complete) {
          // Server confirms every photo is in — reveal the team's gallery.
          setGalleryTeam(res.data.team || teamName);
        } else if (currentPhotoIndex < task.photos.length - 1) {
          setCurrentPhotoIndex(currentPhotoIndex + 1);
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        setError('Failed to upload photo. Please try again.');
      } finally {
        setIsUploading(false);
      }
    }, 'image/jpeg');
  };

  const handleBack = () => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    }
  };

  const handleForward = () => {
    if (currentPhotoIndex < task.photos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    }
  };

  if (!task) return <div className={styles.container}>Loading...</div>;

  return (
    <div className={styles.container}>
      <style>
        {`
          .cameraModal {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            padding: 20px;
            box-sizing: border-box;
          }
          .cameraContainer {
            background: white;
            padding: 20px;
            border-radius: 8px;
            max-width: 90%;
            max-height: 90%;
            overflow: auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
          }
          .cameraSquare {
            width: min(80vw, 60vh);
            aspect-ratio: 1 / 1;
            overflow: hidden;
            border-radius: 4px;
            background: #000;
          }
          .cameraVideo {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }
          .cameraVideo.mirrored {
            transform: scaleX(-1);
          }
          .cameraButtonContainer {
            margin-top: 15px;
            display: flex;
            gap: 10px;
          }
          .captureButton, .secondaryButton {
            border: 3px solid #606060;
            padding: 0.9em 1em;
            font-size: 16px;
            font-weight: 500;
            border-radius: 3px;
            cursor: pointer;
            box-shadow: 6px 6px 0 0 rgba(255, 0, 255, 1), 12px 12px 0 0 rgba(0, 255, 255, 1), -6px -6px 0 0 rgba(255, 255, 0, 1);
          }
          .captureButton {
            background: #ffffff;
            color: #000000;
          }
          .secondaryButton {
            background: transparent;
            color: #606060;
          }
        `}
      </style>
      <header className={styles.header}>
        <a href="https://www.wejam.studio" target="_blank" rel="noopener noreferrer">
          <img src="/logo.png" alt="WeJam Studio" className={styles.logo} />
        </a>
      </header>

      {stage === 'password' && (
        <div className={styles.passwordContainer}>
          <h1 className={styles.header}>Enter Password to Access Photo Task</h1>
          <form onSubmit={handlePasswordSubmit} className={styles.passwordForm}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className={styles.passwordInput}
            />
            <button type="submit" className={styles.passwordButton}>Submit</button>
          </form>
          {error && <div className={styles.passwordErrorMessage}>{error}</div>}
        </div>
      )}

      {stage === 'teamName' && (
        <div className={styles.welcomeContainer}>
          <h1 className={styles.header}>Welcome to the Album Cover challenge!</h1>
          <p className={styles.welcomeText}>{task.welcomeText}</p>
          <form onSubmit={handleTeamNameSubmit} className={styles.teamNameForm}>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter your team name"
              className={styles.teamNameInput}
            />
            <button type="submit" className={styles.teamNameButton}>Start Task</button>
          </form>
          {error && <div className={styles.teamNameErrorMessage}>{error}</div>}
        </div>
      )}

      {stage === 'photos' && (
        <>
          {galleryTeam && (
            <div className={styles.completeBanner}>
              <h2 className={styles.completeHeading}>🎉 All done!</h2>
              <p>Your album covers are ready to view.</p>
              <Link
                to={`/gallery/${taskId}/${encodeURIComponent(galleryTeam)}`}
                className={styles.galleryButton}
              >
                View your album covers →
              </Link>
            </div>
          )}
          <div className={styles.photoContainer}>
            <img
              src={`/Uploads/${taskId}/${task.photos[currentPhotoIndex].filename}`}
              alt={`Task photo ${currentPhotoIndex + 1}`}
              className={styles.taskPhoto}
            />
            <p className={styles.photoCaption}>{task.photos[currentPhotoIndex].caption}</p>
            {isUploading && <div className={styles.uploadingOverlay}>Uploading...</div>}
          </div>
          <div className={styles.uploadContainer}>
            <button
              type="button"
              className={styles.uploadButton}
              onClick={handleOpenCamera}
            >
              {uploadedPhotos.includes(currentPhotoIndex) ? '📸 Re-take Photo' : '📸 Take Photo'}
            </button>
          </div>
          {error && <p className={styles.errorMessage}>{error}</p>}
          {showCamera && (
            <div className="cameraModal">
              <div className="cameraContainer">
                <div className="cameraSquare">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={facingMode === 'user' ? 'cameraVideo mirrored' : 'cameraVideo'}
                  />
                </div>
                <div className="cameraButtonContainer">
                  <button onClick={handleCapture} className="captureButton">Capture</button>
                  <button onClick={handleFlipCamera} className="secondaryButton">Flip</button>
                  <button onClick={handleCancelCamera} className="secondaryButton">Cancel</button>
                </div>
              </div>
            </div>
          )}
          <div className={styles.progressDisplay}>
            <div className={styles.navButtonContainer}>
              {currentPhotoIndex > 0 && (
                <button onClick={handleBack} className={styles.navButton}>
                  ←
                </button>
              )}
            </div>
            <span className={styles.photoNumber}>Photo {currentPhotoIndex + 1} of {task.photos.length}</span>
            <div className={styles.navButtonContainer}>
              {uploadedPhotos.includes(currentPhotoIndex) && currentPhotoIndex < task.photos.length - 1 && (
                <button onClick={handleForward} className={styles.navButton}>
                  →
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoTask;
